<script lang="ts" setup>
  import {
    normalizeObsidianPost,
    useNativePosts,
  } from "~/composables/useBlogPosts";
  import { RESEARCH_PAPERS } from "~/data/research";

  const papers = ref(RESEARCH_PAPERS);
  const { data: posts } = useNativePosts();
  const projects = computed(() =>
    (posts.value ?? [])
      .filter((post) => post.research === true)
      .map((post) => ({ ...normalizeObsidianPost(post), status: post.status })),
  );
  const projectGroups = computed(() =>
    [
      { title: "Research experiments", types: ["experiment"] },
      { title: "Research notes", types: [] },
      { title: "Learning records", types: ["learning-log"] },
    ]
      .map((group) => ({
        title: group.title,
        projects: projects.value.filter((project) =>
          group.types.length
            ? group.types.includes(project.contentType ?? "")
            : !["learning-log", "experiment"].includes(
                project.contentType ?? "",
              ),
        ),
      }))
      .filter((group) => group.projects.length > 0),
  );
  const activeSection = ref<"projects" | "publications">("publications");
  const certificateModal = ref<string | null>(null);

  const openCertificate = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const certificateLink = target.closest<HTMLAnchorElement>(
      "a[data-certificate-url]",
    );
    const certificateUrl = certificateLink?.dataset.certificateUrl;
    if (!certificateUrl) return;

    event.preventDefault();
    certificateModal.value = certificateUrl;
  };

  const sortedPapers = computed(() => {
    const order = { preprint: 0, "in-progress": 1, published: 2 } as const;
    return [...papers.value].sort((a, b) => order[a.status] - order[b.status]);
  });
</script>

<template>
  <section class="space-y-4 mt-0">
    <section class="space-y-3">
      <div
        class="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1 dark:border-zinc-800 dark:bg-zinc-900/50"
      >
        <button
          class="rounded-md px-3 py-1.5 text-xs transition-colors"
          :class="
            activeSection === 'publications'
              ? 'bg-white font-medium text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
              : 'text-zinc-500 dark:text-zinc-400'
          "
          @click="activeSection = 'publications'"
        >
          Publications ({{ papers.length }})
        </button>
        <button
          class="rounded-md px-3 py-1.5 text-xs transition-colors"
          :class="
            activeSection === 'projects'
              ? 'bg-white font-medium text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100'
              : 'text-zinc-500 dark:text-zinc-400'
          "
          @click="activeSection = 'projects'"
        >
          Records ({{ projects.length }})
        </button>
      </div>

      <template v-if="activeSection === 'projects'">
        <p
          v-if="!projects.length"
          class="text-sm text-zinc-500 dark:text-zinc-400"
        >
          No research records are published yet.
        </p>
        <section
          v-for="group in projectGroups"
          :key="group.title"
          class="space-y-3"
        >
          <div>
            <h2 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {{ group.title }}
            </h2>
          </div>

          <div
            v-for="project in group.projects"
            :key="project.id"
            class="space-y-3 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900/50"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <h3
                class="text-sm font-medium leading-snug text-zinc-900 dark:text-zinc-100"
              >
                {{ project.title }}
              </h3>
              <span
                v-if="project.status"
                class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              >
                {{ project.status }}
              </span>
            </div>
            <p class="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              {{ project.description }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="tag in project.tags"
                :key="tag"
                class="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
              >
                {{ tag }}
              </span>
            </div>
            <NuxtLink
              :to="project.href"
              class="inline-flex text-xs font-medium text-zinc-700 underline underline-offset-4 transition-colors hover:text-zinc-950 dark:text-zinc-300 dark:hover:text-white"
            >
              Read the record →
            </NuxtLink>
          </div>
        </section>
      </template>
    </section>

    <template v-if="activeSection === 'publications'">
      <div>
        <!-- <h2 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Publications
        </h2> -->
        <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Papers in journals, conferences and on arXiv.
        </p>
      </div>
      <div
        v-for="(paper, id) in sortedPapers"
        :key="id"
        class="space-y-3 py-4 px-4 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-sm transition-all duration-300 ease-out"
      >
        <!-- Title + status badge -->
        <div class="flex justify-between items-start gap-3">
          <h3
            class="text-sm font-medium text-zinc-900 dark:text-zinc-100 flex-1 leading-snug"
          >
            {{ paper.title }}
            <span v-if="paper.arxivUrl">
              [<a
                :href="paper.arxivUrl"
                target="_blank"
                rel="noopener"
                class="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:underline transition-colors"
                >ArXiv</a
              >]
            </span>
          </h3>

          <span
            v-if="paper.status === 'in-progress'"
            class="flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap"
          >
            In Progress
          </span>
          <span
            v-else-if="paper.status === 'preprint'"
            class="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400 whitespace-nowrap"
          >
            Preprint
          </span>
          <span
            v-else
            class="flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 whitespace-nowrap"
          >
            Published
          </span>
        </div>

        <!-- Authors -->
        <p class="text-xs text-zinc-500 dark:text-zinc-400 italic !mt-0.5">
          <template v-for="(author, idx) in paper.authors" :key="idx">
            <strong
              v-if="author === 'Blessed Madukoma'"
              class="font-semibold text-zinc-700 dark:text-zinc-300"
              >*{{ author }}</strong
            >
            <span v-else>{{ author }}</span>
            <span v-if="idx < paper.authors.length - 1">, </span>
          </template>
        </p>

        <!-- Venue (published papers and preprints) -->
        <div
          v-if="paper.conference"
          class="text-xs text-zinc-500 dark:text-zinc-400 !mt-1"
        >
          <p>
            <span class="font-medium">{{ paper.conference }}</span>
            <span v-if="paper.location"> · {{ paper.location }}</span>
            <span v-if="paper.date"> · {{ paper.date }}.</span>
          </p>
        </div>

        <!-- Abstract -->
        <p
          class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed !mt-0.5"
          v-html="paper.abstract"
          @click="openCertificate"
        ></p>

        <!-- Tags -->
        <div class="flex flex-wrap gap-1.5 !mt-1">
          <span
            v-for="tag in paper.tags"
            :key="tag"
            class="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
          >
            {{ tag }}
          </span>
        </div>

        <!-- PDF and code links -->
        <div
          v-if="paper.pdfUrl || paper.codeUrl || paper.recordUrl"
          class="flex flex-wrap gap-2"
        >
          <NuxtLink
            v-if="paper.recordUrl"
            :to="paper.recordUrl"
            class="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
          >
            Read the Research Record →
          </NuxtLink>
          <a
            v-if="paper.pdfUrl"
            :href="paper.pdfUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
          >
            View Paper →
          </a>
          <a
            v-if="paper.codeUrl"
            :href="paper.codeUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
          >
            Code and Data →
          </a>
        </div>
      </div>
    </template>
  </section>

  <ui-image-modal
    v-if="certificateModal"
    :src="certificateModal"
    alt="SDS 2026 Honorable Mention - Best Paper Award certificate"
    @close="certificateModal = null"
  />
</template>
